import { gql, useLazyQuery } from "@apollo/client";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Tag, Text } from "@ttc3k/trekker";
import { Flex, styled } from "styled-system/jsx";
import { formatMoney } from "shared/utils/formatMoney";
import { formatRenewalDate } from "shared/utils/formatDate";
import { formatTestDate } from "./formatTestDate";

export type TestEmailKind = "REMINDER" | "OVERDUE" | "EXPIRED";

export type TestScenario = {
  state: "ACTIVE" | "RENEWING_SOON" | "OVERDUE" | "EXPIRED";
  available: boolean;
  reason?: string | null;
  availableFrom?: string | null;
  nextRenewalDate?: string | null;
  earlyWindowOpens?: string | null;
};

export type TestMembershipFlow = {
  broadcastId?: string | null;
  broadcastName?: string | null;
  checkoutMode: "TEST" | "LIVE" | "MIXED" | "NONE";
  renewalsEnabled: boolean;
  policy?: {
    renewalType: string;
    term: string;
    termYears?: number | null;
    fixedRenewalMonth?: number | null;
    fixedRenewalDay?: number | null;
    reminderLeadDays: number;
    graceDays: number;
    neverExpires: boolean;
    proRateEnabled: boolean;
  } | null;
  emails: { kind: TestEmailKind; custom: boolean }[];
  scenarios: TestScenario[];
  prorationAvailable: boolean;
};

type ProrationRow = {
  tierName: string;
  optionName?: string | null;
  currency: string;
  fullAmount: number;
  chargeAmount: number;
  pricingKind: string;
  periodStart: string;
  periodEnd: string;
};

const PRORATION = gql`
  query TestRecordProrationPreview($organizationId: String!, $date: String) {
    testRecordProrationPreview(organizationId: $organizationId, date: $date) {
      tierName
      optionName
      currency
      fullAmount
      chargeAmount
      pricingKind
      periodStart
      periodEnd
    }
  }
`;

const todayYmd = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const cardProps = {
  flexDir: "column" as const,
  gap: "150" as const,
  p: "300" as const,
  bgColor: "bg.wa.lightest" as const,
  border: "1px solid" as const,
  borderColor: "border.light" as const,
  borderRadius: "200" as const,
};

type Props = {
  flow: TestMembershipFlow;
  organizationId: string;
  membershipSettingsHref: string;
};

export const MembershipFlowPanel = ({
  flow,
  organizationId,
  membershipSettingsHref,
}: Props) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.TEST_TOOLS.FLOW",
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDate, setPreviewDate] = useState(todayYmd());
  const [loadPreview, { data: previewData, loading: previewLoading, error: previewError }] =
    useLazyQuery(PRORATION, { fetchPolicy: "network-only" });
  const rows: ProrationRow[] = previewData?.testRecordProrationPreview ?? [];

  const policy = flow.policy;
  const policySummary = policy
    ? [
        policy.renewalType === "FIXED_DATE" &&
        policy.fixedRenewalMonth &&
        policy.fixedRenewalDay
          ? t("POLICY.FIXED", {
              date: formatRenewalDate(
                new Date(Date.UTC(2000, policy.fixedRenewalMonth - 1, policy.fixedRenewalDay, 12)),
                "MMM d",
              ),
            })
          : t("POLICY.ANNIVERSARY"),
        t(`POLICY.TERM.${policy.term}`, {
          years: policy.termYears ?? 2,
          defaultValue: policy.term,
        }),
        t("POLICY.REMINDER", { count: policy.reminderLeadDays }),
        policy.neverExpires
          ? t("POLICY.NEVER_EXPIRES")
          : policy.graceDays > 0
            ? t("POLICY.GRACE", { count: policy.graceDays })
            : t("POLICY.NO_GRACE"),
        policy.proRateEnabled ? t("POLICY.PRORATE_ON") : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : t("POLICY.RENEWALS_OFF");

  const runPreview = (date: string) =>
    void loadPreview({ variables: { organizationId, date } });

  return (
    <Flex {...cardProps}>
      <Flex justify="space-between" align="center" gap="200" flexWrap="wrap">
        <Text visual="bodySemiBold">{t("TITLE")}</Text>
        <styled.a
          href={membershipSettingsHref}
          textStyle="smallSemiBold"
          color="text.accent.blue"
        >
          {t("OPEN_SETTINGS")}
        </styled.a>
      </Flex>
      <Text visual="smallRegular" color="text.mid">
        {t("INTRO")}
      </Text>

      <styled.dl display="grid" gridTemplateColumns="max-content 1fr" columnGap="300" rowGap="100">
        <Text as="dt" visual="smallSemiBold">{t("BROADCAST")}</Text>
        <Text as="dd" visual="smallRegular" color={flow.broadcastId ? undefined : "text.error"}>
          {flow.broadcastName || (flow.broadcastId ? t("BROADCAST_UNNAMED") : t("BROADCAST_MISSING"))}
        </Text>

        <Text as="dt" visual="smallSemiBold">{t("CHECKOUT")}</Text>
        <Text
          as="dd"
          visual="smallRegular"
          color={flow.checkoutMode === "TEST" ? undefined : "text.error"}
        >
          {t(`CHECKOUT_MODE.${flow.checkoutMode}`)}
        </Text>

        <Text as="dt" visual="smallSemiBold">{t("RENEWALS")}</Text>
        <Text as="dd" visual="smallRegular">{policySummary}</Text>

        {flow.renewalsEnabled && (
          <>
            <Text as="dt" visual="smallSemiBold">{t("EMAILS")}</Text>
            <styled.dd display="flex" gap="75" flexWrap="wrap">
              {flow.emails.map(email => (
                <Tag key={email.kind} size="sm">
                  {t(`EMAIL.${email.kind}`)} ·{" "}
                  {email.custom ? t("EMAIL_CUSTOM") : t("EMAIL_DEFAULT")}
                </Tag>
              ))}
            </styled.dd>
          </>
        )}
      </styled.dl>

      {flow.prorationAvailable && (
        <Flex flexDir="column" gap="100" mt="100">
          <Button
            size="sm"
            visual="accent.blue"
            mode="light"
            width="auto"
            alignSelf="flex-start"
            onClick={() => {
              const next = !previewOpen;
              setPreviewOpen(next);
              if (next && !previewData) runPreview(previewDate);
            }}
          >
            {previewOpen ? t("PREVIEW_HIDE") : t("PREVIEW_SHOW")}
          </Button>
          {previewOpen && (
            <Flex flexDir="column" gap="100">
              <Flex gap="100" align="center" flexWrap="wrap">
                <Text visual="smallRegular">{t("PREVIEW_DATE")}</Text>
                <Input
                  type="date"
                  w="auto"
                  bg="white"
                  value={previewDate}
                  onChange={e => {
                    setPreviewDate(e.target.value);
                    if (e.target.value) runPreview(e.target.value);
                  }}
                />
              </Flex>
              {previewError && (
                <Text visual="smallRegular" color="text.error">{previewError.message}</Text>
              )}
              {!previewLoading && !previewError && rows.length === 0 && (
                <Text visual="smallRegular" color="text.mid">{t("PREVIEW_EMPTY")}</Text>
              )}
              {rows.map((row, index) => (
                <Text key={`${row.tierName}-${row.optionName ?? ""}-${index}`} visual="smallRegular">
                  <strong>
                    {[row.tierName, row.optionName].filter(Boolean).join(" · ")}
                  </strong>
                  {": "}
                  {t(`PRICING.${row.pricingKind}`, {
                    charge: formatMoney(row.chargeAmount, row.currency),
                    full: formatMoney(row.fullAmount, row.currency),
                    defaultValue: formatMoney(row.chargeAmount, row.currency),
                  })}
                  {" · "}
                  {t("PREVIEW_PERIOD", {
                    start: formatTestDate(row.periodStart),
                    end: formatTestDate(row.periodEnd),
                  })}
                </Text>
              ))}
            </Flex>
          )}
        </Flex>
      )}
    </Flex>
  );
};
